import { useLayoutEffect, useRef } from 'react';

const SPLITTABLE_CONTAINERS: { containerClass: string; itemSelector: string }[] = [
  { containerClass: 'ls-risks-grid', itemSelector: '.ls-risk-card' },
  { containerClass: 'ai-use-case-register-table', itemSelector: 'tr' },
  { containerClass: 'next-steps-body', itemSelector: ':scope > p, :scope > div, :scope > h3, :scope > table' },
  { containerClass: 'stakeholder-card-grid', itemSelector: '.stakeholder-card' },
  { containerClass: 'usecase-detail-group', itemSelector: ':scope > div' },
];

function getSplittableConfig(el: HTMLElement): { containerClass: string; itemSelector: string } | null {
  return SPLITTABLE_CONTAINERS.find((c) => el.classList.contains(c.containerClass)) ?? null;
}


export const useReportPagination = (containerId: string, enabled: boolean = true) => {
  const isPaginatedRef = useRef(false);
  const originalChildrenRef = useRef<Map<HTMLElement, HTMLElement[]>>(new Map());

  useLayoutEffect(() => {
    if (!enabled) return;

    isPaginatedRef.current = false;
    originalChildrenRef.current.clear();

    const paginate = () => {
      if (isPaginatedRef.current) return;

      const container = document.getElementById(containerId);
      if (!container) return;

      const MM_TO_PX = 96 / 25.4;
      const MAX_HEIGHT = Math.round(297 * MM_TO_PX);

      let hasChanges = false;

      const pageHasMeaningfulContent = (page: HTMLElement): boolean => {
        const nonFooterChildren = Array.from(page.children).filter(
          (c) => !(c instanceof HTMLElement) || !c.classList.contains('report-footer'),
        );

        if (nonFooterChildren.length === 0) return false;

        return nonFooterChildren.some((child) => {
          if (!(child instanceof HTMLElement)) return false;

          const txt = (child.textContent ?? '').trim();
          if (txt.length > 0) return true;

          // Structured markup but no text: keep it.
          return !!child.querySelector('table, img, svg, canvas, ul, ol, li');
        });
      };

      const processPage = (page: HTMLElement) => {
        if (page.scrollHeight <= MAX_HEIGHT) return;

        hasChanges = true;
        const footer = page.querySelector('.report-footer');
        const children = Array.from(page.children).filter(
          (c) => !c.classList.contains('report-footer')
        );

        while (page.firstChild) {
          page.removeChild(page.firstChild);
        }

        let currentPage = page;
        const createNewPage = () => {
          const newPage = page.cloneNode(false) as HTMLElement;
          newPage.removeAttribute('id');
          newPage.innerHTML = '';
          newPage.classList.add('report-page-generated');
          currentPage.parentNode?.insertBefore(newPage, currentPage.nextSibling);
          return newPage;
        };

        for (let i = 0; i < children.length; i++) {
          const child = children[i] as HTMLElement;
          currentPage.appendChild(child);

          if (currentPage.scrollHeight <= MAX_HEIGHT) continue;

          const config = getSplittableConfig(child);
          const items = config ? Array.from(child.querySelectorAll(config.itemSelector)) : [];

          if (config && items.length > 1) {
            currentPage.removeChild(child);
            const itemEls = items as HTMLElement[];

            while (child.firstChild) child.removeChild(child.firstChild);
            currentPage.appendChild(child);

            let keepCount = 0;
            for (let k = 0; k < itemEls.length; k++) {
              child.appendChild(itemEls[k]);
              if (currentPage.scrollHeight > MAX_HEIGHT) {
                child.removeChild(itemEls[k]);
                keepCount = k;
                break;
              }
              keepCount = k + 1;
            }

            if (keepCount > 0 && keepCount < itemEls.length) {
              const lastKeptEl = itemEls[keepCount - 1];
              if (lastKeptEl instanceof HTMLElement && /^H[1-6]$/i.test(lastKeptEl.tagName)) {
                child.removeChild(lastKeptEl);
                keepCount--;
              }
            }

            const overflowItems = itemEls.slice(keepCount);
            overflowItems.forEach((el) => el.parentNode?.removeChild(el));
            if (overflowItems.length > 0) {
              if (footer) {
                currentPage.appendChild(footer.cloneNode(true));
              }
              currentPage = createNewPage();
              const newContainer = child.cloneNode(false) as HTMLElement;
              newContainer.innerHTML = '';
              overflowItems.forEach((el) => newContainer.appendChild(el));
              currentPage.appendChild(newContainer);
            }
            continue;
          }

          if (currentPage.children.length === 1) {
            console.warn('Single element too large for page:', child);
            continue;
          }

          const prevChild = currentPage.children[currentPage.children.length - 2];
          const prevIsHeading = prevChild && /^H[1-4]$/i.test(prevChild.tagName);
          const toMove = prevIsHeading ? [prevChild, child] : [child];

          toMove.forEach((el) => currentPage.removeChild(el));
          if (footer) {
            currentPage.appendChild(footer.cloneNode(true));
          }
          currentPage = createNewPage();
          toMove.forEach((el) => currentPage.appendChild(el));
        }

        if (footer) {
          currentPage.appendChild(footer.cloneNode(true));
        }
      };

      const reactPages = Array.from(
        container.querySelectorAll('.report-page:not(.report-page-generated)')
      ) as HTMLElement[];
      reactPages.forEach((page) => {
        originalChildrenRef.current.set(page, Array.from(page.children) as HTMLElement[]);
      });

      let iterations = 0;
      let everChanged = false;
      const maxIterations = 50;
      do {
        hasChanges = false;
        const pages = Array.from(container.querySelectorAll('.report-page'));
        pages.forEach((p) => {
          if (p instanceof HTMLElement) processPage(p);
        });
        if (hasChanges) everChanged = true;
        iterations++;
      } while (hasChanges && iterations < maxIterations);

      if (everChanged) {
        const pages = Array.from(container.querySelectorAll('.report-page'));
        pages.forEach((p) => {
          if (!(p instanceof HTMLElement)) return;
          if (!pageHasMeaningfulContent(p)) p.remove();
        });

        isPaginatedRef.current = true;
      }
    };

    const timer = setTimeout(paginate, 1000);
    const childrenMap = originalChildrenRef.current;

    return () => {
      clearTimeout(timer);

      const container = document.getElementById(containerId);
      if (container && childrenMap.size > 0) {

        Array.from(container.querySelectorAll('.report-page-generated')).forEach((el) => {
          el.parentNode?.removeChild(el);
        });

        childrenMap.forEach((originalChildren, page) => {
          if (!page.isConnected) return;
          while (page.firstChild) page.removeChild(page.firstChild);
          originalChildren.forEach((child) => page.appendChild(child));
        });

        childrenMap.clear();
        isPaginatedRef.current = false;
      }
    };
  }, [containerId, enabled]);
};
